// payment-pdf-generator.service.ts
import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface BalanceSummary {
  Total: number;
  InsEst: number;
  PatEstBal: number;
}

@Injectable({ providedIn: 'root' })
export class PaymentPdfGenerator {
  private static formatDateForDisplay(date: Date): string {
    return formatDate(date, 'EEE, MMM d, yyyy', 'en-US');
  }

  generatePaymentSummary(
    recentVisits: { date: Date; service: string; amount: number }[],
    balance: BalanceSummary,
    clinicInfo?: { displayName?: string; address?: string; phoneNumber?: string }
  ): void {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const w = pdf.internal.pageSize.width;

    // — Header
    const { displayName = 'HEALTH CLINIC', address = '123 Medical Center Drive', phoneNumber = '(555) 123-4567' } = clinicInfo || {};
    pdf.setFontSize(18).text(displayName, 20, 20);
    pdf.setFontSize(11)
       .text(address, 20, 26)
       .text(`Phone: ${phoneNumber}`, 20, 32);

    // — Title & Date
    pdf.setFontSize(16).text('PAYMENT SUMMARY', w - 20, 20, { align: 'right' });
    pdf.setFontSize(10).text(`Date: ${formatDate(new Date(), 'MMM d, yyyy', 'en-US')}`, w - 20, 26, { align: 'right' });

    pdf.setDrawColor(200).line(20, 35, w - 20, 35);

    // — Balance box
    pdf.setFillColor(245, 245, 255)
       .roundedRect(20, 40, w - 40, 30, 3, 3, 'FD')
       .setFontSize(12).text('Balance Summary', 25, 48)
       .setFontSize(10);
    pdf.text(`Total Charges: $${balance.Total.toFixed(2)}`, 25, 55);
    pdf.text(`Insurance Est.: $${balance.InsEst.toFixed(2)}`, 25, 61);
    pdf.text(`Your Balance: $${balance.PatEstBal.toFixed(2)}`, w - 70, 55);

    // — Recent Visits table
    const head = [['Date', 'Service', 'Amount']];
    const body = recentVisits.map(v => [
      PaymentPdfGenerator.formatDateForDisplay(v.date),
      v.service,
      `$${v.amount.toFixed(2)}`
    ]);

    let startY = 80;
    pdf.setFontSize(12).text('Recent Visits', 20, startY);
    autoTable(pdf, {
      head, body,
      startY: startY + 5,
      margin: { left: 20, right: 20 },
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [240,240,240], textColor: [60,60,60] },
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 80 }, 2: { halign: 'right' } }
    });

    // — Footer
    const pageCount = pdf.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      const y = pdf.internal.pageSize.height - 20;
      pdf.setDrawColor(200).line(20, y, w - 20, y);
      pdf.setFontSize(8).setTextColor(150)
         .text('For billing questions, contact (555) 123-4567', w / 2, y + 5, { align: 'center' })
         .text(`Page ${i} of ${pageCount}`,            w / 2, y + 10, { align: 'center' });
    }

    pdf.save('payment-summary.pdf');
  }
}